import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const SWIPE_START_THRESHOLD = 9;
const SWIPE_AXIS_DOMINANCE = 1.18;
const SWIPE_CLOSE_RATIO = 0.27;
const SWIPE_CLOSE_MIN_DISTANCE = 30;
const SWIPE_CLOSE_VELOCITY = 0.52;
const SWIPE_SETTLE_DURATION = 220;
const CLICK_SUPPRESSION_DURATION = 420;
const SWIPE_HARD_IGNORE_SELECTOR = [
  '[data-drawer-swipe-ignore]',
  'input',
  'textarea',
  'select',
  'option',
  '[contenteditable="true"]',
  'iframe',
  'video[controls]',
].join(', ');
const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {{ id: number, startX: number, startY: number, lastX: number, lastTime: number, velocityX: number, axis: 'pending' | 'horizontal' | 'vertical' | 'ignored', distance: number, width: number } | null} */
  #swipePointer = null;

  /** @type {number | undefined} */
  #swipeTimer;

  /** @type {number | undefined} */
  #swipeFrame;

  /** @type {{ distance: number, width: number } | null} */
  #pendingSwipeRender = null;

  /** @type {number} */
  #suppressClickUntil = 0;

  connectedCallback() {
    super.connectedCallback();

    // Native <details> state may be restored by the browser after a language
    // change. Start every freshly rendered header from one deterministic,
    // closed state so the localization panel cannot overlap the main menu.
    this.#resetRestoredDrawerState();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('pointerup', this.#releasePointerFocus);
    this.addEventListener('pointercancel', this.#releasePointerFocus);

    const { menuDrawer } = this.refs;
    menuDrawer.addEventListener('pointerdown', this.#onSwipePointerDown, true);
    menuDrawer.addEventListener('pointermove', this.#onSwipePointerMove, { passive: false, capture: true });
    menuDrawer.addEventListener('pointerup', this.#onSwipePointerEnd, true);
    menuDrawer.addEventListener('pointercancel', this.#onSwipePointerEnd, true);
    menuDrawer.addEventListener('click', this.#onSwipeClickCapture, true);

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('pointerup', this.#releasePointerFocus);
    this.removeEventListener('pointercancel', this.#releasePointerFocus);

    const { menuDrawer } = this.refs;
    menuDrawer.removeEventListener('pointerdown', this.#onSwipePointerDown, true);
    menuDrawer.removeEventListener('pointermove', this.#onSwipePointerMove, true);
    menuDrawer.removeEventListener('pointerup', this.#onSwipePointerEnd, true);
    menuDrawer.removeEventListener('pointercancel', this.#onSwipePointerEnd, true);
    menuDrawer.removeEventListener('click', this.#onSwipeClickCapture, true);

    this.#resetSwipeVisuals();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Touch browsers can retain link/summary focus after the finger is lifted,
   * which makes a press highlight look permanently selected. Keep keyboard
   * focus untouched, but release pointer-created focus once the tap ends.
   * @param {PointerEvent} event
   */
  #releasePointerFocus = (event) => {
    if (event.pointerType === 'mouse' || !(event.target instanceof Element)) return;

    const control = event.target.closest(
      '.aw-mobile-nav__link, .aw-mobile-nav__summary, .aw-mobile-nav__child, .drawer-localization__button'
    );

    if (!(control instanceof HTMLElement)) return;
    requestAnimationFrame(() => control.blur());
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#resetSwipeVisuals();
      // Every fresh drawer opening starts from the calm, collapsed overview.
      // This also neutralises browser-restored <details> state after navigation.
      this.#resetCuratedAccordions();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Remove inline drag transforms only after .menu-open has been removed,
    // so the drawer remains off-screen without a one-frame jump.
    if (details === this.refs.details) {
      this.#resetSwipeVisuals();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Start a possible swipe-to-close gesture. The menu opens from the left,
   * therefore a physical swipe towards the left closes it.
   * @param {PointerEvent} event
   */
  #onSwipePointerDown = (event) => {
    if (!this.isOpen || !event.isPrimary || event.button !== 0 || event.pointerType === 'mouse') return;
    if (!(event.target instanceof Element)) return;
    // Nearly the full menu can originate a swipe. Links, submenu summaries and
    // buttons remain normal taps until a clear leftward horizontal drag wins
    // the axis lock; only text-entry and embedded media stay hard exclusions.
    if (event.target.closest(SWIPE_HARD_IGNORE_SELECTOR)) return;

    this.#resetSwipeVisuals();
    this.#swipePointer = {
      id: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: performance.now(),
      velocityX: 0,
      axis: 'pending',
      distance: 0,
      width: 0,
    };
  };

  /**
   * Let the drawer follow the finger only after a clear horizontal intention.
   * Vertical scrolling remains native and untouched.
   * @param {PointerEvent} event
   */
  #onSwipePointerMove = (event) => {
    const pointer = this.#swipePointer;
    if (!pointer || pointer.id !== event.pointerId || !this.isOpen) return;

    const dx = event.clientX - pointer.startX;
    const dy = event.clientY - pointer.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (pointer.axis === 'pending' && Math.hypot(dx, dy) >= SWIPE_START_THRESHOLD) {
      if (absY > absX * SWIPE_AXIS_DOMINANCE) {
        pointer.axis = 'vertical';
      } else if (absX > absY * SWIPE_AXIS_DOMINANCE) {
        pointer.axis = dx < 0 ? 'horizontal' : 'ignored';
      }
    }

    if (pointer.axis !== 'horizontal') return;
    if (event.cancelable) event.preventDefault();

    const now = performance.now();
    const elapsed = Math.max(1, now - pointer.lastTime);
    pointer.velocityX = (event.clientX - pointer.lastX) / elapsed;
    pointer.lastX = event.clientX;
    pointer.lastTime = now;

    if (pointer.width === 0) pointer.width = Math.max(1, this.refs.menuDrawer.getBoundingClientRect().width);
    const width = pointer.width;
    pointer.distance = Math.min(width, Math.max(0, -dx));

    if (!this.refs.menuDrawer.hasPointerCapture(event.pointerId)) {
      try {
        this.refs.menuDrawer.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is an enhancement; the gesture still works without it.
      }
    }

    this.#queueSwipe(pointer.distance, width);
  };

  /**
   * Complete or cancel the swipe gesture.
   * @param {PointerEvent} event
   */
  #onSwipePointerEnd = (event) => {
    const pointer = this.#swipePointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.#swipePointer = null;

    if (this.refs.menuDrawer.hasPointerCapture(event.pointerId)) {
      try {
        this.refs.menuDrawer.releasePointerCapture(event.pointerId);
      } catch {
        // The browser may already have released capture on pointercancel.
      }
    }

    if (pointer.axis !== 'horizontal') return;

    const endX = Number.isFinite(event.clientX) ? event.clientX : pointer.lastX;
    const elapsed = Math.max(1, performance.now() - (pointer.lastTime || performance.now()));
    const terminalVelocity = Math.min(pointer.velocityX, (endX - pointer.lastX) / elapsed);
    const width = pointer.width || Math.max(1, this.refs.menuDrawer.getBoundingClientRect().width);
    const closeDistance = Math.min(96, width * SWIPE_CLOSE_RATIO);
    const shouldClose =
      event.type !== 'pointercancel' &&
      (pointer.distance >= closeDistance ||
        (pointer.distance >= SWIPE_CLOSE_MIN_DISTANCE && terminalVelocity <= -SWIPE_CLOSE_VELOCITY));

    if (pointer.distance >= SWIPE_START_THRESHOLD) {
      this.#suppressClickUntil = performance.now() + CLICK_SUPPRESSION_DURATION;
    }

    this.#renderSwipe(pointer.distance, width);
    this.#settleSwipe(shouldClose, width);
  };

  /**
   * Prevent the synthetic click that touch browsers can emit after a drag.
   * @param {MouseEvent} event
   */
  #onSwipeClickCapture = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  /**
   * Coalesce pointermove rendering so transform/backdrop writes occur once per frame.
   * @param {number} distance
   * @param {number} width
   */
  #queueSwipe(distance, width) {
    this.#pendingSwipeRender = { distance, width };
    if (this.#swipeFrame !== undefined) return;

    this.#swipeFrame = requestAnimationFrame(() => {
      this.#swipeFrame = undefined;
      const pending = this.#pendingSwipeRender;
      this.#pendingSwipeRender = null;
      if (pending) this.#renderSwipe(pending.distance, pending.width);
    });
  }

  #cancelSwipeFrame() {
    if (this.#swipeFrame !== undefined) cancelAnimationFrame(this.#swipeFrame);
    this.#swipeFrame = undefined;
    this.#pendingSwipeRender = null;
  }

  /**
   * @param {number} distance
   * @param {number} width
   */
  #renderSwipe(distance, width) {
    const progress = Math.min(1, distance / Math.max(1, width));
    const { menuDrawer } = this.refs;
    const backdrop = this.refs.details.querySelector(':scope > .menu-drawer__backdrop');

    menuDrawer.classList.add('menu-drawer--swipe-dragging');
    menuDrawer.style.setProperty('transition', 'none');
    menuDrawer.style.setProperty('transform', `translate3d(${-distance}px, 0, 0)`);

    if (backdrop instanceof HTMLElement) {
      backdrop.classList.add('menu-drawer__backdrop--swipe-dragging');
      backdrop.style.setProperty('transition', 'none');
      backdrop.style.setProperty('opacity', `${1 - progress * 0.72}`);
    }
  }

  /**
   * @param {boolean} close
   * @param {number} width
   */
  #settleSwipe(close, width) {
    this.#cancelSwipeFrame();
    const { menuDrawer } = this.refs;
    const backdrop = this.refs.details.querySelector(':scope > .menu-drawer__backdrop');
    const duration = REDUCED_MOTION_QUERY.matches ? 0 : SWIPE_SETTLE_DURATION;
    const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

    menuDrawer.classList.remove('menu-drawer--swipe-dragging');
    menuDrawer.style.setProperty('transition', `transform ${duration}ms ${easing}`);
    menuDrawer.style.setProperty('transform', close ? `translate3d(${-width}px, 0, 0)` : 'translate3d(0, 0, 0)');

    if (backdrop instanceof HTMLElement) {
      backdrop.classList.remove('menu-drawer__backdrop--swipe-dragging');
      backdrop.style.setProperty('transition', `opacity ${duration}ms ease`);
      backdrop.style.setProperty('opacity', close ? '0' : '1');
    }

    this.#clearSwipeTimer();
    this.#swipeTimer = window.setTimeout(() => {
      this.#swipeTimer = undefined;
      if (close) {
        this.#close(this.refs.details);
      } else {
        this.#resetSwipeVisuals();
      }
    }, duration + 24);
  }

  #clearSwipeTimer() {
    if (this.#swipeTimer === undefined) return;
    window.clearTimeout(this.#swipeTimer);
    this.#swipeTimer = undefined;
  }

  #resetSwipeVisuals() {
    this.#clearSwipeTimer();
    this.#cancelSwipeFrame();
    this.#swipePointer = null;

    const { menuDrawer } = this.refs;
    menuDrawer.classList.remove('menu-drawer--swipe-dragging');
    menuDrawer.style.removeProperty('transition');
    menuDrawer.style.removeProperty('transform');
    menuDrawer.style.removeProperty('will-change');

    const backdrop = this.refs.details.querySelector(':scope > .menu-drawer__backdrop');
    if (backdrop instanceof HTMLElement) {
      backdrop.classList.remove('menu-drawer__backdrop--swipe-dragging');
      backdrop.style.removeProperty('transition');
      backdrop.style.removeProperty('opacity');
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Close the curated native accordions before the main drawer becomes visible.
   * Browsers can restore native <details> state across navigation, so markup
   * alone is not sufficient to guarantee a closed first view.
   */
  #resetCuratedAccordions() {
    const accordions = this.querySelectorAll('.aw-mobile-nav__accordion[open], #drawer-localization[open]');

    accordions.forEach((accordion) => {
      if (accordion instanceof HTMLDetailsElement) reset(accordion);
    });

    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
  }

  /**
   * Neutralize state restored by browser history/form navigation before the
   * drawer becomes interactive. This is especially important after changing
   * language, where the nested localization <details> can otherwise reappear
   * without the matching drawer classes.
   */
  #resetRestoredDrawerState() {
    const restoredDetails = this.querySelectorAll(
      '.aw-mobile-nav__accordion[open], #drawer-localization[open], #drawer-localization.menu-open'
    );

    restoredDetails.forEach((details) => {
      if (details instanceof HTMLDetailsElement) reset(details);
    });

    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    if (this.refs.details.hasAttribute('open') || this.refs.details.classList.contains('menu-open')) {
      reset(this.refs.details);
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
